// Đọc bí mật (API key, PAT, token) qua stdin thay vì argv.
//
// VÌ SAO: argv của mọi tiến trình đọc được bằng `ps -ef` từ user khác trên cùng
// máy, và bị process manager / CI ghi vào log. stdin không nằm trong bảng tiến
// trình. Quy ước cờ: `--<domain>-<field>-stdin` bật đường này.

import type { Readable } from "node:stream";

/** Giới hạn kích thước để một stdin không bao giờ đóng không ăn hết bộ nhớ. */
const MAX_SECRET_BYTES = 64 * 1024;

/** Pipe mở mà quá `idleTimeoutMs` không nhận thêm byte nào (cha quên `end()`). */
export class StdinIdleTimeoutError extends Error {
  constructor(readonly idleTimeoutMs: number) {
    super(`stdin không nhận byte nào trong ${idleTimeoutMs} ms`);
    this.name = "StdinIdleTimeoutError";
  }
}

export interface ReadStdinUntilEofOptions {
  /** Hạn nhàn rỗi (ms) tính từ lúc bắt đầu / byte cuối; vắng ⇒ chờ EOF mãi. */
  idleTimeoutMs?: number;
}

/**
 * Đọc stdin tới EOF, trả NGUYÊN VĂN (không trim). Trần 64 KiB; hạn nhàn rỗi
 * tuỳ chọn. Lỗi/hết hạn ⇒ ngừng đọc (pause) để không giữ event loop.
 */
export function readStdinUntilEof(
  stream: Readable = process.stdin,
  options: ReadStdinUntilEofOptions = {},
): Promise<string> {
  // Review vòng sửa 04b L-b: stream đã `end` (mọi byte đã bị tiêu thụ) ⇒ sẽ
  // không bao giờ có `end` nữa — trả ngay thay vì treo.
  if (stream.readableEnded) return Promise.resolve("");
  return new Promise((resolve, reject) => {
    const chunks: Buffer[] = [];
    let total = 0;
    let settled = false;
    let timer: NodeJS.Timeout | undefined;
    const finish = (err?: Error) => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      stream.off("data", onData);
      stream.off("end", onEnd);
      stream.off("error", finish);
      // L-b: lỗi stdin muộn sau khi đã xong không được thành uncaught exception.
      stream.on("error", () => {});
      if (err === undefined) {
        resolve(Buffer.concat(chunks).toString("utf8"));
        return;
      }
      stream.pause();
      reject(err);
    };
    const armIdleTimer = () => {
      const ms = options.idleTimeoutMs;
      if (ms === undefined) return;
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(() => finish(new StdinIdleTimeoutError(ms)), ms);
    };
    const onData = (chunk: unknown) => {
      const buf = Buffer.isBuffer(chunk) ? chunk : Buffer.from(String(chunk));
      total += buf.length;
      if (total > MAX_SECRET_BYTES) {
        finish(
          new Error(`Bí mật đọc từ stdin vượt ${MAX_SECRET_BYTES} byte — nhiều khả năng sai nguồn`),
        );
        return;
      }
      chunks.push(buf);
      armIdleTimer();
    };
    const onEnd = () => finish();
    stream.on("data", onData);
    stream.once("end", onEnd);
    stream.once("error", finish);
    armIdleTimer();
  });
}

/**
 * Đọc toàn bộ stdin, trả chuỗi đã cắt khoảng trắng hai đầu.
 *
 * Cắt `\n` cuối là bắt buộc: `echo "$KEY" | avatar …` luôn kèm newline, để lọt
 * thì key sai mà thông báo lỗi lại nói "key không hợp lệ" — cực khó truy.
 */
export async function readSecretFromStdin(stream: Readable = process.stdin): Promise<string> {
  return (await readStdinUntilEof(stream)).trim();
}

/**
 * Đọc bí mật nếu cờ `*-stdin` được bật, ngược lại trả `undefined`.
 *
 * Gộp vào đây để 3 cụm T1/T2/T3 không mỗi nơi tự viết một nhánh `if`.
 */
export async function readSecretIfRequested(
  useStdin: boolean | undefined,
  stream: Readable = process.stdin,
): Promise<string | undefined> {
  if (useStdin !== true) return undefined;
  const secret = await readSecretFromStdin(stream);
  return secret === "" ? undefined : secret;
}
